import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Upload, LogOut, Trash2, Megaphone, Plus, Video } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { ATCFeedManager } from "@/components/admin/ATCFeedManager";

export default function Admin() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [videoFiles, setVideoFiles] = useState<File[]>([]);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [deletingSubmission, setDeletingSubmission] = useState(false);
  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [newAnnouncement, setNewAnnouncement] = useState("");
  const [editingAnnouncement, setEditingAnnouncement] = useState<any | null>(null);
  const [editAnnouncementText, setEditAnnouncementText] = useState("");
  const [savingAnnouncement, setSavingAnnouncement] = useState(false);

  useEffect(() => {
    checkAdminAccess();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchSubmissions();
      fetchAnnouncements();
    }
  }, [isAdmin]);

  const checkAdminAccess = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        navigate("/auth");
        return;
      }

      const { data: roleData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', session.user.id)
        .eq('role', 'admin')
        .maybeSingle();

      if (!roleData) {
        toast({
          title: "Access denied",
          description: "You don't have admin permissions",
          variant: "destructive",
        });
        navigate("/");
        return;
      }

      setIsAdmin(true);
    } catch (error) {
      console.error('Error checking admin access:', error);
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const handleVideoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setVideoFiles(Array.from(e.target.files));
    }
  };

  const handleVideoUpload = async () => {
    if (videoFiles.length === 0) return;

    try {
      setUploadingVideo(true);
      
      for (const file of videoFiles) {
        const fileExt = file.name.split('.').pop();
        const fileName = `background-video.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from('videos')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: true
          });

        if (uploadError) {
          throw uploadError;
        }
      }

      toast({
        title: "Success",
        description: `${videoFiles.length} video(s) uploaded successfully`,
      });

      setVideoFiles([]);
    } catch (error: any) {
      console.error('Error uploading videos:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to upload videos",
        variant: "destructive",
      });
    } finally {
      setUploadingVideo(false);
    }
  };

  const fetchSubmissions = async () => {
    try {
      const { data, error } = await supabase
        .from('contact_submissions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSubmissions(data || []);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast({
        title: "Error",
        description: "Failed to load contact submissions",
        variant: "destructive",
      });
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const { data, error } = await supabase
        .from('announcements')
        .select('*')
        .order('display_order', { ascending: true});

      if (error) throw error;
      setAnnouncements(data || []);
    } catch (error) {
      console.error('Error fetching announcements:', error);
      toast({
        title: "Error",
        description: "Failed to load announcements",
        variant: "destructive",
      });
    }
  };

  const handleDeleteSubmission = async (id: string) => {
    try {
      setDeletingSubmission(true);
      const { error } = await supabase
        .from('contact_submissions')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Submission deleted successfully",
      });

      fetchSubmissions();
    } catch (error: any) {
      console.error('Error deleting submission:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete submission",
        variant: "destructive",
      });
    } finally {
      setDeletingSubmission(false);
    }
  };

  const handleAddAnnouncement = async () => {
    if (!newAnnouncement.trim()) return;

    try {
      const { error } = await supabase
        .from('announcements')
        .insert({
          message: newAnnouncement,
          is_active: true,
          display_order: announcements.length,
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Announcement added successfully",
      });

      setNewAnnouncement("");
      fetchAnnouncements();
    } catch (error: any) {
      console.error('Error adding announcement:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to add announcement",
        variant: "destructive",
      });
    }
  };

  const handleToggleAnnouncement = async (id: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('announcements')
        .update({ is_active: !currentStatus })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Announcement ${!currentStatus ? 'activated' : 'deactivated'}`,
      });

      fetchAnnouncements();
    } catch (error: any) {
      console.error('Error toggling announcement:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to toggle announcement",
        variant: "destructive",
      });
    }
  };

  const handleDeleteAnnouncement = async (id: string) => {
    try {
      const { error } = await supabase
        .from('announcements')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Announcement deleted successfully",
      });

      fetchAnnouncements();
    } catch (error: any) {
      console.error('Error deleting announcement:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete announcement",
        variant: "destructive",
      });
    }
  };

  const openEditDialog = (announcement: any) => {
    setEditingAnnouncement(announcement);
    setEditAnnouncementText(announcement.message);
  };

  const handleSaveEdit = async () => {
    if (!editingAnnouncement || !editAnnouncementText.trim()) return;

    try {
      setSavingAnnouncement(true);
      const { error } = await supabase
        .from('announcements')
        .update({ message: editAnnouncementText })
        .eq('id', editingAnnouncement.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Announcement updated successfully",
      });

      setEditingAnnouncement(null);
      setEditAnnouncementText("");
      fetchAnnouncements();
    } catch (error: any) {
      console.error('Error updating announcement:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update announcement",
        variant: "destructive",
      });
    } finally {
      setSavingAnnouncement(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-xl">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white p-8">
      <div className="max-w-6xl mx-auto space-y-8">
        <div className="flex justify-between items-center">
          <h1 className="text-4xl font-bold">Admin Dashboard</h1>
          <Button onClick={handleSignOut} variant="outline">
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </div>

        <ATCFeedManager />

        <Card className="glass-card p-8">
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold mb-2">Background Video</h2>
              <p className="text-muted-foreground">
                Upload a background video for the main page
              </p>
            </div>

            <div className="space-y-4">
              <div className="border-2 border-dashed border-white/20 rounded-lg p-8 text-center">
                <input
                  type="file"
                  accept="video/*"
                  multiple
                  onChange={handleVideoFileChange}
                  className="hidden"
                  id="video-upload"
                  disabled={uploadingVideo}
                />
                <label
                  htmlFor="video-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <Video className="h-12 w-12 mb-4 text-muted-foreground" />
                  <p className="text-lg font-medium mb-2">
                    Click to select video files
                  </p>
                  <p className="text-sm text-muted-foreground">
                    MP4, WebM, or other video formats
                  </p>
                </label>
              </div>

              {videoFiles.length > 0 && (
                <div className="space-y-2">
                  <p className="font-medium">Selected files ({videoFiles.length}):</p>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {videoFiles.map((file, index) => (
                      <div
                        key={index}
                        className="text-sm text-muted-foreground p-2 bg-white/5 rounded"
                      >
                        {file.name}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Button
                onClick={handleVideoUpload}
                disabled={uploadingVideo || videoFiles.length === 0}
                className="w-full"
                size="lg"
              >
                {uploadingVideo ? (
                  "Uploading..."
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload {videoFiles.length} Video{videoFiles.length !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </div>
          </div>
        </Card>

        <Card className="glass-card p-8">
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold mb-2">Announcements</h2>
              <p className="text-muted-foreground">
                Manage site-wide announcements
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex gap-2">
                <Textarea
                  placeholder="Enter announcement text..."
                  value={newAnnouncement}
                  onChange={(e) => setNewAnnouncement(e.target.value)}
                  className="flex-1"
                />
                <Button onClick={handleAddAnnouncement} disabled={!newAnnouncement.trim()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add
                </Button>
              </div>

              <div className="space-y-2">
                {announcements.map((announcement) => (
                  <div
                    key={announcement.id}
                    className="flex items-center justify-between p-4 bg-white/5 rounded-lg"
                  >
                    <div className="flex-1">
                      <p className={announcement.is_active ? "" : "opacity-50"}>
                        {announcement.message}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(announcement)}
                      >
                        <Megaphone className="h-4 w-4" />
                      </Button>
                      <Button
                        variant={announcement.is_active ? "secondary" : "default"}
                        size="sm"
                        onClick={() => handleToggleAnnouncement(announcement.id, announcement.is_active)}
                      >
                        {announcement.is_active ? "Deactivate" : "Activate"}
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteAnnouncement(announcement.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>

        <Card className="glass-card p-8">
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold mb-2">Contact Submissions</h2>
              <p className="text-muted-foreground">
                View and manage contact form submissions ({submissions.length} total)
              </p>
            </div>

            <div className="space-y-4">
              {submissions.map((submission) => (
                <div
                  key={submission.id}
                  className="p-4 bg-white/5 rounded-lg space-y-2"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{submission.name}</p>
                      <p className="text-sm text-muted-foreground">{submission.email}</p>
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteSubmission(submission.id)}
                      disabled={deletingSubmission}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-sm">{submission.message}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(submission.created_at).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>

      <Dialog open={!!editingAnnouncement} onOpenChange={(open) => !open && setEditingAnnouncement(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Announcement</DialogTitle>
            <DialogDescription>
              Update the announcement text
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={editAnnouncementText}
            onChange={(e) => setEditAnnouncementText(e.target.value)}
            placeholder="Announcement text"
            className="min-h-[100px]"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditingAnnouncement(null)}
              disabled={savingAnnouncement}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={savingAnnouncement || !editAnnouncementText.trim()}
            >
              {savingAnnouncement ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
